// hooks
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import useHttp from "hooks/use-http";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
} from "@mui/material";

// material icons
import { FilterAlt, Delete } from "@mui/icons-material";
import { RiNumbersFill } from "react-icons/ri";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import DatePickerCM from "components/shared/DatePicker";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import ProgressBarCM from "components/shared/ProgressBar";

// helper
import GenerateHeadCell from "helper/generate-head-cell";
import BackButton from "components/shared/BackButton";

// styled component
import {
  StyledStack,
  StyledDateBox,
  StyledMenuItem,
  StyledGridIcons,
} from "./excellScores.style";

// request methods
import { getEducationYearRequest, getScoresTestRequest } from "api/requests";

// constants
import { PAGINATION_LIMIT } from "constants";

import { useNavigate, useParams } from "react-router-dom";
import { convertNum } from "helper/convert-num";

import { StyledLink } from "components/shared/Link";
import { PY_IMG_BASE_URL } from "constants";

// excell scores head cells
const headCells = GenerateHeadCell("exam_score_list");

export default function DisplayExcellScoresList() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const params = useParams();

  const {
    request,
    loading,
    isUploading,
    setLoading,
    setUploadProgress,
    uploadProgress,
  } = useHttp(false);

  // states for filtered
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  // excellScores data state
  const [excellScoresData, setExcellScoresData] = useState({
    data: [],
    count: 0,
  });

  // education years data state
  const [educationalYears, setEducationalYears] = useState([]);
  // page state for pagination
  const [page, setPage] = useState(1);
  const [className, setClassName] = useState("");

  // date filter handler
  const onFromDateHandler = (value) =>
    setFromDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  const onToDateHandler = (value) =>
    setToDate(
      value
        .format("YYYY-MM-DD")
        .split("-")
        .map((item) => convertNum(item))
        .join("-")
    );

  // get all items
  const getExcellScoresList = async () => {
    setLoading(true);
    const educationYearsRes = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });

    if (educationYearsRes) {
      setEducationalYears(educationYearsRes);
      setSelectedEducationalYear({
        value: educationYearsRes[educationYearsRes.length - 2],
      });
    }

    const values = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      class_id: params.class_id,
      educational_year: selectedEducationalYear.value ?? null,
      from_date: fromDate ?? null,
      to_date: toDate ?? null,
    };

    !values.from_date && delete values.from_date;
    !values.to_date && delete values.to_date;

    const res = await request({
      url: getScoresTestRequest,
      values,
    });

    if (res) {
      if (res?.count < 1) {
        return setExcellScoresData({
          count: res?.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد." }],
        });
      }
      let transformedData = res?.data?.map((items) => ({
        id: items.id,
        score_date: items?.date?.string,
        answer_sheet_type: items?.answer_sheet_type + " تایی " ?? "ثبت نشد",
        number_of_questions: items?.number_of_questions ?? "ثبت نشد",
        student_numbers: (
          <Tooltip title="نمایش" placement="top">
            <IconButton
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher_class/exam/scores/${items.id}`);
              }}
            >
              <RiNumbersFill />
            </IconButton>
          </Tooltip>
        ),
        answer_sheet: (
          <>
            {items.answer_sheet_image == null ? (
              ""
            ) : (
              <StyledLink
                to={PY_IMG_BASE_URL + items.answer_sheet_image}
                target="_blank"
                style={{ marginTop: "2px", marginBottom: "2px" }}
              >
                <Button color="success" variant="outlined">
                  دانلود
                </Button>
              </StyledLink>
            )}
          </>
        ),
        enable:
          items?.is_enabled == true ? (
            <span style={{ color: "green" }}>فعال</span>
          ) : (
            <span style={{ color: "red" }}>غیرفعال</span>
          ),
      }));
      setClassName(res.data[0].class_model?.title);
      setExcellScoresData({
        data: transformedData,
        count: res?.count,
      });
    }
    setLoading(false);
  };
  // get items without filter
  const getExcellScoresListWithoutFiltered = async () => {
    setLoading(true);
    setFromDate("");
    setToDate("");
    setSelectedEducationalYear("");

    const educationYearsRes = await request({
      url: getEducationYearRequest,
      isMulty: true,
    });

    if (educationYearsRes) {
      setEducationalYears(educationYearsRes);
      setSelectedEducationalYear({
        value: educationYearsRes[educationYearsRes.length - 2],
      });
    }

    const res = await request({
      url: getScoresTestRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
        class_id: params.class_id,
      },
    });

    if (res) {
      if (res?.count < 1) {
        return setExcellScoresData({
          count: res?.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد." }],
        });
      }
      let transformedData = res?.data?.map((items) => ({
        id: items.id,
        score_date: items?.date?.string,
        answer_sheet_type: items?.answer_sheet_type + " تایی " ?? "ثبت نشد",
        number_of_questions: items?.number_of_questions ?? "ثبت نشد",
        student_numbers: (
          <Tooltip title="نمایش" placement="top">
            <IconButton
              variant="primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher_class/exam/scores/${items.id}`);
              }}
            >
              <RiNumbersFill />
            </IconButton>
          </Tooltip>
        ),
        answer_sheet: (
          <>
            {items.answer_sheet_image == null ? (
              ""
            ) : (
              <StyledLink
                to={PY_IMG_BASE_URL + items.answer_sheet_image}
                target="_blank"
                style={{ marginTop: "2px", marginBottom: "2px" }}
              >
                <Button color="success" variant="outlined">
                  دانلود
                </Button>
              </StyledLink>
            )}
          </>
        ),
        enable:
          items?.is_enabled == true ? (
            <span style={{ color: "green" }}>فعال</span>
          ) : (
            <span style={{ color: "red" }}>غیرفعال</span>
          ),
      }));
      setClassName(res.data[0].class_model?.title);
      setExcellScoresData({
        data: transformedData,
        count: res?.count,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getExcellScoresList();
  }, [page]);

  return (
    <DrawerCM title="لیست آزمون تستی " $styledHeader>
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item my={2} xs={12}>
              <BackButton />
            </Grid>
          </Grid>
          <StyledStack>
            <Grid container>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_educationalYear"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        error={Boolean(errors.educationalYear)}
                        helperText={errors.educationalYear?.message}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onFromDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="از تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="from_created_at"
                          {...register("from_created_at")}
                          error={Boolean(errors.from_created_at)}
                          helperText={errors.from_created_at?.message}
                          value={fromDate ? fromDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <StyledDateBox>
                  <DatePickerCM
                    onChange={onToDateHandler}
                    render={(value, openCalendar) => {
                      return (
                        <TextField
                          label="تا تاریخ"
                          variant="outlined"
                          fullWidth
                          size="small"
                          id="to_created_at"
                          {...register("to_created_at")}
                          error={Boolean(errors.to_created_at)}
                          helperText={errors.to_created_at?.message}
                          value={toDate ? toDate.split("-").join("/") : ""}
                          onClick={openCalendar}
                        />
                      );
                    }}
                  />
                </StyledDateBox>
              </Grid>
              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getExcellScoresList}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getExcellScoresListWithoutFiltered}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title={"کلاس " + " " + className}
                rows={excellScoresData.data}
                headCells={headCells}
                addTestScore
                Id={params.class_id}
                page={page}
                colspan={excellScoresData.count === 0 ? "8" : ""}
                fontSize="14px"
                cellFontSize="14px"
              />
            </Grid>
            {excellScoresData.data.length < 1 ? (
              ""
            ) : (
              <Grid item xs={12}>
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={excellScoresData.count}
                />
              </Grid>
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}
